import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { Reveal, Kicker } from '../components/Reveal';
import { SEO } from '../components/SEO';
import { Banner } from './Home';

const PHOTOS = [
  'evento-notte.jpg', 'dsc06760.jpg', 'archi.jpg', 'foto-evento-iqvia.jpg',
  'catering.jpg', 'aperitivo.jpg', 'gustavo28-aperitivo-058-1783614719670.jpg', 'gustavo28-aperitivo-038-1784636004091.jpg',
  '5v9a5009-1784636004647.jpg', '5v9a5091-1784636006510.jpg', 'gustavo28-aperitivo-065-1784636005272.jpg',
  'burrata.jpg', 'dsc06950-1784636861339.jpg', '5v9a4760-1784636862117.jpg', 'dsc06910-1784636862683.jpg',
  'dsc06927-1784636865072.jpg', 'gnocchi.jpg', 'cantina.jpg', 'dsc06725.jpg', 'location-evento.jpg',
  'piano-terra.jpg', '5v9a4740.jpg', 'spazio-vuoto.jpg', 'spazio-vuoto-wide.jpg',
];

export default function Gallery() {
  const { t } = useLanguage();
  const [open, setOpen] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      if (open === null) return;
      if (e.key === 'Escape') setOpen(null);
      if (e.key === 'ArrowRight') setOpen((o) => (o + 1) % PHOTOS.length);
      if (e.key === 'ArrowLeft') setOpen((o) => (o - 1 + PHOTOS.length) % PHOTOS.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div data-testid="gallery-page">
      <SEO title={t.meta.galleryTitle} description={t.meta.galleryDesc} />

      <section className="bg-emerald-deep grain relative pt-40 pb-24 md:pb-32" data-testid="gallery-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <Kicker light>{t.galleryPage.kicker}</Kicker>
            <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory tracking-tight max-w-3xl">{t.galleryPage.title}</h1>
            <p className="mt-6 max-w-2xl text-ivory/70 text-base md:text-lg leading-relaxed">{t.galleryPage.intro}</p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="columns-2 md:columns-3 gap-4">
          {PHOTOS.map((p, i) => (
            <Reveal key={p} delay={(i % 3) * 0.05} className="break-inside-avoid mb-4">
              <button
                data-testid={`gallery-photo-${i}`}
                onClick={() => setOpen(i)}
                className="img-frame block w-full rounded-sm shadow-[0_8px_30px_rgba(37,79,87,0.10)] cursor-zoom-in"
                aria-label={`${t.galleryPage.title} ${i + 1}`}
              >
                <img src={`/images/${p}`} alt={`Gustavo 28 — ${t.galleryPage.title} ${i + 1}`} className="w-full object-cover" loading="lazy" />
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      <AnimatePresence>
        {open !== null && (
          <motion.div
            data-testid="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[80] bg-emerald-deep/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-12"
            onClick={() => setOpen(null)}
          >
            <button
              data-testid="lightbox-close"
              onClick={() => setOpen(null)}
              className="absolute top-5 right-5 text-ivory/80 hover:text-terracotta transition-colors duration-300 p-2 z-10"
              aria-label="Close"
            >
              <X size={30} />
            </button>
            <button
              data-testid="lightbox-prev"
              onClick={(e) => { e.stopPropagation(); setOpen((open - 1 + PHOTOS.length) % PHOTOS.length); }}
              className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 text-ivory/80 hover:text-terracotta transition-colors duration-300 p-2 z-10"
              aria-label="Previous"
            >
              <ChevronLeft size={40} />
            </button>
            <motion.img
              key={open}
              data-testid="lightbox-image"
              src={`/images/${PHOTOS[open]}`}
              alt={`Gustavo 28 — ${t.galleryPage.title} ${open + 1}`}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="max-h-[85vh] max-w-full object-contain rounded-sm shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              data-testid="lightbox-next"
              onClick={(e) => { e.stopPropagation(); setOpen((open + 1) % PHOTOS.length); }}
              className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 text-ivory/80 hover:text-terracotta transition-colors duration-300 p-2 z-10"
              aria-label="Next"
            >
              <ChevronRight size={40} />
            </button>
            <span data-testid="lightbox-counter" className="absolute bottom-6 left-1/2 -translate-x-1/2 text-ivory/60 text-sm tracking-widest">
              {open + 1} / {PHOTOS.length}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      <Banner title={t.home.bannerTitle} text={t.home.bannerText} cta={t.home.bannerCta} />
    </div>
  );
}
